/**
 * api/stream.js — helper de streaming SSE (Server-Sent Events) para o Live Board.
 *
 * Problema: o EventSource nativo do browser não permite header Authorization.
 * Solução: fetch + ReadableStream com parsing manual de SSE, passando Bearer JWT.
 *
 * Contrato do endpoint:
 *   GET /executions/{id}/stream?after={seq}
 *   Content-Type: text/event-stream
 *
 * Formato SSE:
 *   event: cli
 *   data: {"seq":1,"type":"cli","payload":{"line":"[input] Iniciando..."}}
 *
 *   event: kanban
 *   data: {"seq":2,"type":"kanban","payload":{...}}
 *
 *   event: metrics
 *   data: {"seq":3,"type":"metrics","payload":{...}}
 *
 *   event: gate
 *   data: {"seq":4,"type":"gate","payload":{...}}
 *
 *   : keep-alive   ← heartbeat (ignorar)
 *
 * Rehidratação sem gap/duplicata:
 *   - Cada evento tem um número de sequência (seq).
 *   - No reconnect, reenvia ?after={último seq recebido}.
 *   - O backend só retorna eventos com seq > after.
 *
 * Backoff exponencial:
 *   base 1s, multiplicador 2x, máx 30s, jitter ±20%.
 *
 * Segurança (OWASP A09):
 *   - NUNCA expor ou logar wallet_txn_id ou qualquer campo sensível.
 *   - Token não exposto em query string.
 *   - 401/403 são tratados amigavelmente, sem vazar detalhes internos.
 *
 * Standards: Clean, NFR (resiliência, conectividade).
 */

// ─── Tipos (JSDoc) ─────────────────────────────────────────────────────────────

/**
 * @typedef {'cli'|'kanban'|'metrics'|'gate'|'sandbox'} SseEventType
 */

/**
 * @typedef {object} SseEvent
 * @property {number} seq
 * @property {SseEventType} type
 * @property {object} payload
 */

/**
 * @typedef {object} StreamCallbacks
 * @property {(event: SseEvent) => void}    onEvent      — chamado a cada evento parseado
 * @property {(state: ConnectionState) => void} onStateChange — chamado ao mudar estado da conexão
 * @property {(err: StreamError) => void}   [onError]    — chamado em erros permanentes (sem mais tentativas)
 */

/**
 * @typedef {'connecting'|'connected'|'reconnecting'|'closed'} ConnectionState
 */

/**
 * @typedef {object} StreamError
 * @property {number|null} status   — HTTP status se disponível
 * @property {string} message       — mensagem legível
 * @property {boolean} isForbidden  — true se 401/403
 * @property {boolean} isPermanent  — true se não haverá retry
 */

// ─── Constantes ───────────────────────────────────────────────────────────────

/** Delay base do backoff em ms. */
const BACKOFF_BASE_MS = 1_000;

/** Multiplicador do backoff exponencial. */
const BACKOFF_MULTIPLIER = 2;

/** Delay máximo do backoff em ms (30s). */
const BACKOFF_MAX_MS = 30_000;

/** Fator de jitter (±20%). */
const BACKOFF_JITTER = 0.2;

/** Número máximo de tentativas antes de desistir. 0 = infinito. */
const MAX_RETRIES = 0;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Calcula o próximo delay de backoff exponencial com jitter.
 * @param {number} attempt — número da tentativa (0-based)
 * @returns {number} delay em ms
 */
export function calcBackoffDelay(attempt) {
  const exp   = BACKOFF_BASE_MS * Math.pow(BACKOFF_MULTIPLIER, attempt);
  const capped = Math.min(exp, BACKOFF_MAX_MS);
  const jitter  = capped * BACKOFF_JITTER * (Math.random() * 2 - 1);
  return Math.max(0, Math.round(capped + jitter));
}

/**
 * Filtra campos sensíveis de um payload antes de processar.
 * OWASP A09: nunca exibir ou logar wallet_txn_id.
 *
 * @param {object} payload
 * @returns {object}
 */
export function sanitizePayload(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const { wallet_txn_id: _dropped, ...safe } = payload;
  return safe;
}

/**
 * Formata uma linha de CLI a partir do payload de um evento `cli`.
 * Backend usa `message`; aceita `line` por compatibilidade. Prefixa o agente.
 * @param {object} payload
 * @returns {string}
 */
export function formatCliLine(payload) {
  const p = payload ?? {};
  const delegacao = formatDelegacaoLine(p);
  if (delegacao) return delegacao;
  const text = p.line ?? p.message ?? '';
  if (!text) return '';
  if (typeof text === 'string' && text.trimStart().startsWith('[')) return text;
  const who = p.agent_id || p.actor_id || p.phase;
  return who ? `[${who}] ${text}` : String(text);
}

/**
 * A5 (FR-5b): delegação a subagente dentro da camada. O evento `cli` chega com
 * `kind: "delegacao" | "delegacao_fim"`, `agent_id` = subagente e `delegado_por` =
 * agente da camada. A linha sai ASSINADA PELO SUBAGENTE e mostra "A → B":
 *   início → `[B] ↪ A → B · camada: pedido`
 *   fim    → `[B] ✓ A → B · concluído (N s)`
 * O pedido e a duração vêm da mensagem do worker; se ela não casar com o formato
 * conhecido, a mensagem inteira é mantida (nada se perde).
 * @param {object} payload
 * @returns {string} '' quando o payload não é uma delegação
 */
export function formatDelegacaoLine(payload) {
  const p = payload ?? {};
  if (p.kind !== 'delegacao' && p.kind !== 'delegacao_fim') return '';
  const de = String(p.delegado_por ?? '').trim();
  const para = String(p.agent_id ?? '').trim();
  if (!de || !para) return '';
  const msg = String(p.message ?? p.line ?? '').trim();
  const camada = p.camada ? `${p.camada}: ` : '';
  if (p.kind === 'delegacao') {
    const m = msg.match(/delegou a\s+`?[^`:]+`?\s*:\s*([\s\S]*)$/);
    const pedido = (m ? m[1] : msg).trim();
    return `[${para}] ↪ ${de} → ${para}${pedido || camada ? ` · ${camada}${pedido}` : ''}`.trimEnd();
  }
  const d = msg.match(/\(([^()]+)\)\s*$/);
  return `[${para}] ✓ ${de} → ${para} · concluído${d ? ` (${d[1]})` : ''}`;
}

/**
 * Converte qualquer evento do pipeline numa linha legível para o log do Live
 * Board — para o usuário ver TUDO que está acontecendo em background.
 * @param {SseEvent} event
 * @returns {string}
 */
export function formatSystemLine(event) {
  const p = event?.payload ?? {};
  const who = p.agent_id || p.actor_id || '';
  const pre = who ? `[${who}] ` : '';
  const msg = p.message ?? p.summary ?? p.detail ?? '';
  const step = p.step_name ?? p.step ?? '';
  switch (event?.type) {
    case 'execution_started': return `${pre}▶ Execução iniciada`;
    case 'squad_ready':       return `${pre}✓ Squad montado${p.agent_count ? ` — ${p.agent_count} agentes` : ''}`;
    case 'requirements_ready':return `${pre}✓ Requisitos extraídos${p.count ? ` — ${p.count} itens` : ''}`;
    case 'step_completed':    return `${pre}✓ Passo concluído${step ? `: ${step}` : ''}`;
    case 'codegen_progress':  return `${pre}⚙ ${msg || 'Geração de código em progresso'}`;
    case 'gate':
    case 'gate_a_pending':    return `⏸ Gate A aguardando aprovação`;
    case 'gate_a_decided':    return `${pre}Gate A: ${p.decision ?? p.result ?? 'decidido'}`;
    case 'gate_b_pending':    return `⏸ Gate B aguardando aprovação`;
    case 'gate_b_decided':    return `${pre}Gate B: ${p.decision ?? p.result ?? 'decidido'}`;
    case 'execution_done':    return `✅ Execução concluída`;
    case 'execution_failed':  return `❌ Execução falhou${msg ? `: ${msg}` : ''}`;
    case 'system':            return `${pre}${msg}`;
    default:                  return msg ? `${pre}${msg}${step ? ` (${step})` : ''}` : '';
  }
}

/**
 * Parseia um bloco de texto SSE (separado por \n\n) em evento.
 * Retorna null se não for um evento válido (ex.: heartbeat, comentário).
 *
 * @param {string} block — bloco bruto do SSE (linhas separadas por \n)
 * @returns {SseEvent|null}
 */
export function parseSseBlock(block) {
  /** @type {Record<string,string>} */
  const fields = {};

  for (const line of block.split('\n')) {
    if (line.startsWith(':')) continue; // comentário / heartbeat

    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;

    const fieldName = line.slice(0, colonIdx).trim();
    const fieldValue = line.slice(colonIdx + 1).trimStart();

    if (fieldName === 'data') {
      // Multiple data lines are concatenated per SSE spec
      fields.data = fields.data ? `${fields.data}\n${fieldValue}` : fieldValue;
    } else if (fieldName) {
      fields[fieldName] = fieldValue;
    }
  }

  if (!fields.data) return null;

  let parsed;
  try {
    parsed = JSON.parse(fields.data);
  } catch {
    return null;
  }

  if (typeof parsed.seq !== 'number') return null;

  // Aceita AMBOS os formatos de fio:
  //   • aninhado  { seq, type, payload:{...} }        (contrato antigo/tests)
  //   • achatado  { seq, event_type, ...campos }      (o backend real emite este)
  const type = parsed.type ?? parsed.event_type;
  if (!type) return null;

  let payload;
  if (parsed.payload && typeof parsed.payload === 'object') {
    payload = parsed.payload;
  } else {
    // Formato achatado: o resto dos campos É o payload (fora os metadados).
    const { seq: _s, type: _t, event_type: _et, execution_id: _e, ts: _ts, ...rest } = parsed;
    payload = rest;
  }

  return /** @type {SseEvent} */ ({
    seq:     parsed.seq,
    type,
    payload: sanitizePayload(payload) ?? {},
  });
}

// ─── openStream ───────────────────────────────────────────────────────────────

/**
 * openStream — abre o stream SSE para uma execução via fetch + ReadableStream.
 *
 * Retorna um handle com método `close()` para encerrar o stream.
 * Reconecta automaticamente com backoff e rehidratação (after={seq}).
 *
 * @param {object} opts
 * @param {string} opts.executionId        — ID da execução
 * @param {() => string|null} opts.getToken — devolve o access token JWT atual
 * @param {string} opts.baseUrl            — base URL da API (ex.: http://localhost:8000)
 * @param {StreamCallbacks} opts.callbacks
 * @param {typeof fetch} [opts.fetchImpl]  — injeta fetch (para testes)
 * @returns {{ close: () => void }}
 */
export function openStream({ executionId, getToken, baseUrl, callbacks, fetchImpl }) {
  const _fetch = fetchImpl ?? globalThis.fetch;

  let closed    = false;
  let attempt   = 0;
  let lastSeq   = -1;
  let abortCtrl = null;
  let retryTimer = null;

  /** Encerra o stream de forma limpa. */
  function close() {
    closed = true;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    abortCtrl?.abort();
    callbacks.onStateChange('closed');
  }

  /** Notifica mudança de estado de forma segura. */
  function setState(/** @type {ConnectionState} */ state) {
    callbacks.onStateChange(state);
  }

  /** Constrói a URL com o parâmetro after. */
  function buildUrl() {
    const base  = `${baseUrl}/executions/${executionId}/stream`;
    const query = lastSeq >= 0 ? `?after=${lastSeq}` : '';
    return `${base}${query}`;
  }

  /** Lê os chunks do ReadableStream e parseia eventos SSE. */
  async function readStream(reader) {
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events são separados por \n\n (ou \r\n\r\n)
      const parts = buffer.split(/\r?\n\r?\n/);
      // O último part pode estar incompleto — mantém no buffer
      buffer = parts.pop() ?? '';

      for (const block of parts) {
        const trimmed = block.trim();
        if (!trimmed) continue;
        if (trimmed.startsWith(':')) continue; // heartbeat puro

        const event = parseSseBlock(trimmed);
        if (event) {
          lastSeq = event.seq;
          callbacks.onEvent(event);
        }
      }
    }
  }

  /** Tenta conectar ao stream. */
  async function connect() {
    if (closed) return;

    setState(attempt === 0 ? 'connecting' : 'reconnecting');

    abortCtrl = new AbortController();

    const token = getToken();

    /** @type {HeadersInit} */
    const headers = {
      Accept: 'text/event-stream',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response;
    try {
      response = await _fetch(buildUrl(), {
        method:  'GET',
        headers,
        signal:  abortCtrl.signal,
        // Cache nunca para SSE
        cache:   'no-store',
      });
    } catch (fetchErr) {
      if (closed) return;
      if (fetchErr?.name === 'AbortError') return;

      scheduleReconnect({
        status:      null,
        message:     'Sem conexão com o servidor',
        isForbidden: false,
        isPermanent: false,
      });
      return;
    }

    if (closed) return;

    // ── Tratar erros HTTP ────────────────────────────────────────────────────
    if (!response.ok) {
      const isForbidden = response.status === 401 || response.status === 403;

      // Tenta ler o body como problem+json para mensagem amigável
      let message = `Erro ${response.status} ao conectar ao stream`;
      try {
        const ct = response.headers.get('Content-Type') ?? '';
        if (ct.includes('json')) {
          const body = await response.json();
          // Nunca expor campos sensíveis do body de erro
          if (body.title) message = body.title;
          else if (body.detail) message = body.detail;
        }
      } catch {
        // ignora
      }

      const err = {
        status:      response.status,
        message,
        isForbidden,
        isPermanent: isForbidden, // 401/403 não recebem retry automático
      };

      if (isForbidden) {
        // Dispara evento global para que o AuthProvider possa reagir
        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('nava:session-expired', {
            detail: { url: response.url },
          }));
        }
        if (callbacks.onError) callbacks.onError(err);
        close();
        return;
      }

      scheduleReconnect(err);
      return;
    }

    // ── Conexão bem-sucedida ─────────────────────────────────────────────────
    attempt = 0;
    setState('connected');

    const reader = response.body?.getReader();
    if (!reader) {
      scheduleReconnect({
        status:      null,
        message:     'Stream body não disponível',
        isForbidden: false,
        isPermanent: false,
      });
      return;
    }

    try {
      await readStream(reader);
    } catch (readErr) {
      if (closed) return;
      if (readErr?.name === 'AbortError') return;
      // Erro de leitura → reconectar
    } finally {
      try { reader.cancel(); } catch { /* ignora */ }
    }

    if (!closed) {
      // Stream terminou normalmente (servidor fechou) → reconectar
      scheduleReconnect({
        status:      null,
        message:     'Stream encerrado pelo servidor',
        isForbidden: false,
        isPermanent: false,
      });
    }
  }

  /**
   * Agenda um reconnect com backoff.
   * Se MAX_RETRIES > 0 e atingimos o limite, reporta erro permanente.
   */
  function scheduleReconnect(err) {
    if (closed) return;

    if (MAX_RETRIES > 0 && attempt >= MAX_RETRIES) {
      const permanent = { ...err, isPermanent: true };
      if (callbacks.onError) callbacks.onError(permanent);
      close();
      return;
    }

    setState('reconnecting');

    const delay = calcBackoffDelay(attempt);
    attempt++;

    retryTimer = setTimeout(() => {
      retryTimer = null;
      connect();
    }, delay);
  }

  // Inicia a primeira conexão
  connect();

  return { close };
}

// ─── Hook React: useExecutionStream ──────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from 'react';
import { useContext } from 'react';
import { ApiContext } from './ApiContext';

// IMPORTANTE: usar `import.meta.env.VITE_API_URL` SEM optional chaining — o Vite só
// substitui estaticamente essa forma exata no build. Com `import.meta?.env?.` o
// valor não é injetado e cai no fallback localhost:8000 (cross-origin) → o SSE
// dispara preflight CORS que retorna 405 e a conexão nunca abre (Live Board vazio).
// Igual ao client.js: no build é `/api` (mesma origem via nginx) → sem CORS.
const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

/**
 * useExecutionStream — hook que conecta ao SSE de uma execução e acumula
 * os eventos em estado React, substituindo os setTimeout do Live Board.
 *
 * Estado exposto:
 *   - connectionState: 'connecting'|'connected'|'reconnecting'|'closed'
 *   - cliLines:   string[]           — linhas do log ao vivo (eventos cli)
 *   - kanbanEvents: SseEvent[]       — eventos kanban (para mover cards)
 *   - metrics:    object|null        — último snapshot de métricas
 *   - gateEvent:  SseEvent|null      — último evento de gate
 *   - sandboxEvent: SseEvent|null    — último evento sandbox (sandbox.spawned etc.)
 *   - streamError: StreamError|null  — erro permanente (401/403 etc.)
 *
 * Reconecta automaticamente com backoff e rehidratação (after={seq}).
 * Nunca expõe wallet_txn_id ou dados sensíveis do sandbox.
 *
 * Evento sandbox esperado (payload):
 *   { status: 'spawned'|'ready'|'terminated', is_real: boolean, ttl_sec?: number }
 *   NÃO incluir: sandbox_run_id, VM IDs, IPs internos.
 *
 * @param {string|null} executionId
 * @returns {{
 *   connectionState: ConnectionState,
 *   cliLines: string[],
 *   kanbanEvents: SseEvent[],
 *   metrics: object|null,
 *   gateEvent: SseEvent|null,
 *   sandboxEvent: SseEvent|null,
 *   streamError: StreamError|null,
 * }}
 */
export function useExecutionStream(executionId) {
  const apiCtx = useContext(ApiContext);

  const [connectionState, setConnectionState] = useState(/** @type {ConnectionState} */ ('connecting'));
  const [cliLines,        setCli]             = useState(/** @type {string[]} */ ([]));
  const [kanbanEvents,    setKanban]           = useState(/** @type {SseEvent[]} */ ([]));
  const [metrics,         setMetrics]          = useState(/** @type {object|null} */ (null));
  const [gateEvent,       setGateEvent]        = useState(/** @type {SseEvent|null} */ (null));
  const [sandboxEvent,    setSandboxEvent]      = useState(/** @type {SseEvent|null} */ (null));
  const [streamError,     setStreamError]      = useState(/** @type {StreamError|null} */ (null));

  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'cli': {
        // Backend usa `message` (não `line`); mantém compat com `line`.
        const line = formatCliLine(event.payload);
        if (line) {
          setCli((prev) => [...prev, line]);
        }
        break;
      }
      case 'kanban': {
        setKanban((prev) => [...prev, event]);
        break;
      }
      case 'metrics': {
        // Sanitize já foi feito em parseSseBlock; aplica novamente por segurança
        setMetrics(sanitizePayload(event.payload));
        break;
      }
      case 'gate':
      case 'gate_a_pending':
      case 'gate_a_decided':
      case 'gate_b_pending':
      case 'gate_b_decided': {
        setGateEvent(event);
        const sys = formatSystemLine(event);
        if (sys) setCli((prev) => [...prev, sys]);
        break;
      }
      case 'sandbox': {
        // Sanitize: nunca expor sandbox_run_id, IPs ou outros campos sensíveis.
        // Mantemos apenas status e is_real.
        const { status, is_real, ttl_sec } = event.payload ?? {};
        setSandboxEvent({
          ...event,
          payload: { status, is_real, ttl_sec },
        });
        break;
      }
      default: {
        // Todo o resto (squad_ready, requirements_ready, step_completed,
        // codegen_progress, execution_started/done/failed, system…) vira linha
        // no log — para o Live Board mostrar TUDO que acontece em background.
        const sys = formatSystemLine(event);
        if (sys) setCli((prev) => [...prev, sys]);
        break;
      }
    }
  }, []);

  const handleStateChange = useCallback((state) => {
    setConnectionState(state);
  }, []);

  const handleError = useCallback((err) => {
    setStreamError(err);
  }, []);

  useEffect(() => {
    if (!executionId) return;

    // Reseta estado ao trocar de execução
    setCli([]);
    setKanban([]);
    setMetrics(null);
    setGateEvent(null);
    setSandboxEvent(null);
    setStreamError(null);
    setConnectionState('connecting');

    // getToken: tenta via ApiContext._getToken (injetado pelo ApiProvider estendido)
    // ou fallback para getAccessToken direto do tokenStorage
    const getToken = apiCtx?._getToken ?? (() => null);

    const handle = openStream({
      executionId,
      getToken,
      baseUrl: BASE_URL,
      callbacks: {
        onEvent:       handleEvent,
        onStateChange: handleStateChange,
        onError:       handleError,
      },
    });

    return () => handle.close();
  }, [executionId, apiCtx, handleEvent, handleStateChange, handleError]);

  return {
    connectionState,
    cliLines,
    kanbanEvents,
    metrics,
    gateEvent,
    sandboxEvent,
    streamError,
  };
}
